<template>
  <v-navigation-drawer
    id="appDrawer"
    :mini-variant.sync="mini"
    fixed
    dark
    app
    v-model="drawer"
    width="260"
  >
    <!-- Brand Header -->
    <div class="drawer-brand">
      <div class="drawer-brand__logo">
        <img v-bind:src="computeLogo" height="32" width="32" alt="edPass" class="drawer-brand__img" />
      </div>
      <div v-if="!mini" class="drawer-brand__text">
        <span class="drawer-brand__name">edPass</span>
        <span class="drawer-brand__sub">Admin Portal</span>
      </div>
    </div>

    <v-divider class="drawer-divider"></v-divider>

    <vue-perfect-scrollbar
      class="drawer-menu--scroll"
      :settings="scrollSettings"
    >
      <v-list dense expand class="drawer-nav">
        <template v-for="(item, i) in menus">
          <!-- group with subitems -->
          <v-list-group
            v-if="item.items"
            :key="item.name"
            :group="item.group"
            :prepend-icon="item.icon"
            no-action="no-action"
            class="drawer-group"
          >
            <v-list-tile slot="activator" ripple="ripple" class="drawer-tile">
              <v-list-tile-content>
                <v-list-tile-title class="drawer-tile__title">{{ item.title }}</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
            <template v-for="(subItem, i) in item.items">
              <!-- sub group -->
              <v-list-group
                v-if="subItem.items"
                :key="subItem.name"
                :group="subItem.group"
                sub-group="sub-group"
              >
                <v-list-tile slot="activator" ripple="ripple" class="drawer-tile">
                  <v-list-tile-content>
                    <v-list-tile-title class="drawer-tile__title">{{ subItem.title }}</v-list-tile-title>
                  </v-list-tile-content>
                </v-list-tile>
                <v-list-tile
                  v-for="(grand, i) in subItem.children"
                  :key="i"
                  :to="genChildTarget(item, grand)"
                  :href="grand.href"
                  ripple="ripple"
                  class="drawer-tile drawer-tile--child"
                >
                  <v-list-tile-content>
                    <v-list-tile-title class="drawer-tile__title">{{ grand.title }}</v-list-tile-title>
                  </v-list-tile-content>
                </v-list-tile>
              </v-list-group>
              <!-- child item -->
              <v-list-tile
                v-else
                :key="i"
                :to="genChildTarget(item, subItem)"
                :href="subItem.href"
                :disabled="subItem.disabled"
                :target="subItem.target"
                ripple="ripple"
                class="drawer-tile drawer-tile--child"
              >
                <v-list-tile-content>
                  <v-list-tile-title class="drawer-tile__title">
                    <span>{{ subItem.title }}</span>
                  </v-list-tile-title>
                </v-list-tile-content>
                <v-list-tile-action v-if="subItem.action">
                  <v-icon :class="[subItem.actionClass || 'success--text']">{{
                    subItem.action
                  }}</v-icon>
                </v-list-tile-action>
              </v-list-tile>
            </template>
          </v-list-group>

          <!-- Section header -->
          <v-subheader v-else-if="item.header" :key="i" class="drawer-subheader">
            {{ item.header }}
          </v-subheader>

          <v-divider v-else-if="item.divider" :key="i" class="drawer-divider--nav"></v-divider>

          <!-- top-level link -->
          <v-list-tile
            v-else
            :to="!item.href ? { name: item.name } : null"
            :href="item.href"
            ripple="ripple"
            :disabled="item.disabled"
            :target="item.target"
            rel="noopener"
            :key="item.name"
            class="drawer-tile"
          >
            <v-list-tile-action v-if="item.icon">
              <v-icon class="drawer-tile__icon">{{ item.icon }}</v-icon>
            </v-list-tile-action>
            <v-list-tile-content>
              <v-list-tile-title class="drawer-tile__title">{{ item.title }}</v-list-tile-title>
            </v-list-tile-content>
            <v-list-tile-action v-if="item.subAction">
              <v-icon class="success--text">{{ item.subAction }}</v-icon>
            </v-list-tile-action>
          </v-list-tile>
        </template>
      </v-list>
    </vue-perfect-scrollbar>

    <!-- Mini toggle -->
    <div class="drawer-footer">
      <v-btn icon flat small class="drawer-mini-btn" @click="mini = !mini">
        <v-icon small>{{ mini ? 'chevron_right' : 'chevron_left' }}</v-icon>
      </v-btn>
    </div>
  </v-navigation-drawer>
</template>

<script>
import menu from "@/api/menu";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
export default {
  name: "app-drawer",
  components: {
    VuePerfectScrollbar
  },
  props: {
    expanded: {
      type: Boolean,
      default: true
    }
  },
  data: () => ({
    mini: false,
    drawer: true,
    menus: menu,
    scrollSettings: {
      maxScrollbarLength: 160
    }
  }),
  computed: {
    computeGroupActive() {
      return true;
    },
    computeLogo() {
      return "/static/eduPass.jpeg";
    },
    sideToolbarColor() {
      return this.$vuetify.options.extra.sideNav;
    }
  },
  created() {
    window.getApp.$on("APP_DRAWER_TOGGLED", () => {
      this.drawer = !this.drawer;
    });
  },
  methods: {
    genChildTarget(item, subItem) {
      if (subItem.href) return;
      if (subItem.component) {
        return { name: subItem.component };
      }
      return { name: `${item.group}/${subItem.name}` };
    }
  }
};
</script>

<style lang="stylus">
#appDrawer
  background: #1e293b !important
  overflow: hidden

  .drawer-brand
    display: flex
    align-items: center
    padding: 18px 20px
    background: linear-gradient(135deg, #1565C0 0%, #0d47a1 100%)
    height: 64px
    min-height: 64px

  .drawer-brand__logo
    flex-shrink: 0
    width: 36px
    height: 36px
    border-radius: 10px
    overflow: hidden
    background: rgba(255,255,255,0.15)
    display: flex
    align-items: center
    justify-content: center

  .drawer-brand__img
    object-fit: cover
    border-radius: 8px

  .drawer-brand__text
    margin-left: 12px
    display: flex
    flex-direction: column

  .drawer-brand__name
    font-family: 'Inter', 'Roboto', sans-serif
    font-size: 16px
    font-weight: 700
    color: #ffffff
    line-height: 1.2
    letter-spacing: -0.3px

  .drawer-brand__sub
    font-size: 10px
    font-weight: 400
    color: rgba(255,255,255,0.55)
    letter-spacing: 0.5px
    text-transform: uppercase

  .drawer-divider
    border-color: rgba(255,255,255,0.08) !important
    margin: 0

  .drawer-menu--scroll
    height: calc(100vh - 64px - 48px)
    overflow: auto

  .drawer-nav
    padding: 8px 0

  .drawer-subheader
    font-family: 'Inter', 'Roboto', sans-serif !important
    font-size: 10px !important
    font-weight: 700 !important
    letter-spacing: 1.2px !important
    text-transform: uppercase !important
    color: rgba(255,255,255,0.35) !important
    padding: 16px 20px 6px !important
    height: auto !important

  .drawer-divider--nav
    border-color: rgba(255,255,255,0.06) !important
    margin: 6px 16px !important

  .drawer-tile
    height: 44px !important
    margin: 1px 8px !important
    border-radius: 8px !important
    width: calc(100% - 16px) !important

    &:hover
      background: rgba(255,255,255,0.06) !important

    &--child
      padding-left: 24px !important

  .drawer-tile__icon
    font-size: 20px !important
    color: rgba(255,255,255,0.6) !important
    width: 24px

  .drawer-tile__title
    font-family: 'Inter', 'Roboto', sans-serif !important
    font-size: 14px !important
    font-weight: 500 !important
    color: rgba(255,255,255,0.8) !important
    letter-spacing: 0.1px

  .v-list__tile--active
    background: rgba(255,255,255,0.1) !important

    .drawer-tile__icon
      color: #64B5F6 !important

    .drawer-tile__title
      color: #ffffff !important
      font-weight: 600 !important

  .v-list__group--active
    .v-list__group__header
      background: rgba(255,255,255,0.06) !important

  .drawer-group
    .v-list__group__header__prepend-icon .v-icon
      color: rgba(255,255,255,0.6) !important

  .drawer-footer
    position: absolute
    bottom: 0
    left: 0
    right: 0
    height: 48px
    display: flex
    align-items: center
    justify-content: flex-end
    padding: 0 12px
    background: rgba(0,0,0,0.15)
    border-top: 1px solid rgba(255,255,255,0.06)

  .drawer-mini-btn
    color: rgba(255,255,255,0.4) !important

    &:hover
      color: rgba(255,255,255,0.8) !important
</style>
